import { DOCUMENT } from '@angular/common';
import { DestroyRef, Injectable, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpClient, HttpParams } from '@angular/common/http';
import { firstValueFrom, interval } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  DEFAULT_SYSTEM_STATUS,
  parseSystemStatusPayload,
  type SystemStatusPayload,
} from './system-status.model';

@Injectable({
  providedIn: 'root',
})
export class SystemStatusService {
  private readonly http = inject(HttpClient);
  private readonly document = inject(DOCUMENT);
  private readonly statusSignal = signal<SystemStatusPayload>({ ...DEFAULT_SYSTEM_STATUS });

  readonly status = this.statusSignal.asReadonly();

  isMaintenance(): boolean {
    return this.statusSignal().enabled;
  }

  getDisplayPayload(): SystemStatusPayload {
    return this.statusSignal();
  }

  private resolveStatusUrl(): string {
    const configured =
      typeof environment.systemStatusUrl === 'string' ? environment.systemStatusUrl.trim() : '';
    if (configured.length > 0) {
      return configured;
    }
    const pageUrl = this.document.defaultView?.location?.href;
    if (typeof pageUrl === 'string' && pageUrl.length > 0) {
      return new URL('/system-status.json', pageUrl).href;
    }
    return '/system-status.json';
  }

  async loadInitial(): Promise<void> {
    await this.fetchAndApply();
  }

  async refresh(): Promise<void> {
    await this.fetchAndApply();
  }

  /**
   * Polls while the maintenance page is shown so ops can clear `enabled` without a full reload.
   */
  startPollingWhileOnMaintenancePage(destroyRef: DestroyRef): void {
    interval(60_000)
      .pipe(takeUntilDestroyed(destroyRef))
      .subscribe(() => {
        void this.refresh();
      });
  }

  private async fetchAndApply(): Promise<void> {
    const url = this.resolveStatusUrl();
    const params = new HttpParams().set('t', String(Date.now()));
    try {
      const text = await firstValueFrom(this.http.get(url, { params, responseType: 'text' }));
      const trimmed = text.trim();
      if (trimmed.startsWith('<')) {
        console.warn(
          'System status: received HTML instead of JSON from',
          url,
          '— usually system-status.json is missing, or IIS rewrote this URL to index.html. ' +
            'Confirm the file exists in wwwroot and redeploy with the updated web.config (static file exclusions).',
        );
        this.statusSignal.set({ ...DEFAULT_SYSTEM_STATUS });
        return;
      }
      const raw = JSON.parse(trimmed) as unknown;
      this.statusSignal.set(parseSystemStatusPayload(raw));
    } catch (error: unknown) {
      console.warn('System status fetch failed:', error);
      this.statusSignal.set({ ...DEFAULT_SYSTEM_STATUS });
    }
  }
}
